import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { mainurl } from '../../MainUrl';
import Header from '../../component/layout/Header';
import Footer from '../../component/layout/Footer';
import Page from '../../component/layout/Page';
import Title from '../../component/layout/Title';
import { Row, Col, Card, CardBody, Spinner, Input, Button, FormGroup, Label } from 'reactstrap';
import { notification } from '../../component/hocs/notification';
import DataTable from 'react-data-table-component';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ViewRaw = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
   const [fromDate, setFromDate] = useState(new Date());  // Default to today's date
  const [toDate, setToDate] = useState(new Date());
  const [selectedProduct, setSelectedProduct] = useState('');
  const [productNames, setProductNames] = useState([]);

  const formatDateForDisplay = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const formatDateForBackend = (date) => {
    if (!date || isNaN(date)) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;  // Correct format for backend
  };

  const get_list_handler = () => {
    setLoading(true);
    axios.get(mainurl + 'view_raw')
      .then((response) => {
        const list = response.data.data.flatMap((row, index) => {
          const sortedDetails = row?.raw_stock_details?.sort((a, b) => a.stock_details_id - b.stock_details_id) || [];
          return sortedDetails.map(stock => ({
            sl: index + 1,
            Raw_Material_Name: row?.raw_material_name ?? '',
            StockIn_UOM: stock.stock_in > 0 ? `${stock.stock_in} ${stock.uom_master?.uom ?? 'N/A'}` : '0',
            StockOut_UOM: stock.stock_out > 0 ? `${stock.stock_out} ${stock.uom_master?.uom ?? 'N/A'}` : '0',
            Current_Stock_UOM: `${stock.current_stock} ${stock.uom_master?.uom ?? 'N/A'}`,
            Transaction_Date: stock.transaction_date ? formatDateForDisplay(stock.transaction_date) : 'N/A',
            reference_number: stock.ref_no ?? 'N/A',
            ...row,
          }));
        });
        setTableData(list);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error in View Raw API:', error);
        setLoading(false);
        notification('error', 'Failed to fetch raw material data');
      });
  };

  const fetchProductNames = () => {
    axios.get(mainurl + 'raw_list')
      .then((response) => {
        setProductNames(response.data.data || []);
      })
      .catch((error) => {
        console.error('Error fetching product names:', error);
        notification('error', 'Failed to fetch product names');
      });
  };

  const getFilteredStatusData = () => {
    if (!fromDate || !toDate) {
      notification('warning', 'Please fill in all fields');
      return;
    }
    setLoading(true);
    setTableData([]);

    const requestBody = {
      from_date: formatDateForBackend(fromDate),
      to_date: formatDateForBackend(toDate),
      raw_material_id: selectedProduct === 'all' ? 0 : Number(selectedProduct),
    };

    axios.post(mainurl + 'raw_status', requestBody)
      .then((response) => {
        if (!response.data?.success || !response.data?.data) {
          notification('error', 'No data available for the given filters');
          setLoading(false);
          return;
        }
        const list = response.data.data.flatMap((row, index) => {
          const sortedDetails = row.raw_stock_details || [];
          return sortedDetails.map((stock) => ({
            sl: index + 1,
            Raw_Material_Name: row.raw_material_name || 'N/A',
            StockIn_UOM: stock.stock_in ? `${stock.stock_in} ${stock.stock_in_uom || 'N/A'}` : '0',
            StockOut_UOM: stock.stock_out ? `${stock.stock_out} ${stock.stock_out_uom || 'N/A'}` : '0',
            Current_Stock_UOM: `${stock.current_stock} ${stock.current_stock_uom || 'N/A'}`,
            Transaction_Date: stock.transaction_date ? formatDateForDisplay(stock.transaction_date) : 'N/A',
            reference_number: stock.ref_no || 'N/A',
          }));
        });
        setTableData(list);
        setLoading(false);
      })
      .catch((error) => {
        notification('error', 'Failed to fetch filtered status data');
        setLoading(false);
      });
  };

  useEffect(() => {
    get_list_handler();
    fetchProductNames();
  }, []);

  // Custom table styling to improve alignment and make header more prominent
  const custTableStyles = {
    rows: {
      highlightOnHover: true,
      style: {
        textAlign: 'center', // Align row content to center
      },
    },
    headCells: {
      style: {
        fontWeight: 'bold',
        backgroundColor: '#FF0000', // Set header background color to red
        color: 'white', // Set header text color to white
        textAlign: 'center', // Center-align header cells
      },
    },
    cells: {
      style: {
        //padding: '10px', // Add padding to table cells
        textAlign: 'center', // Align cell content to the center by default
      },
    },
  };

  // Custom column styles to add extra gap between columns
  const columnStyles = {
    sl: {
      width: '50px',
      //paddingRight: '80px', // Increased space after "Sl No" for more gap
    },
    Raw_Material_Name: {
      //paddingRight: '300px', // Adjust spacing for Raw Material Name column
    },
    Transaction_Date: {
      //paddingLeft: '80px', // Adjust spacing for Transaction Date column
    },
    reference_number: {
      //paddingRight: '80px', // Adjust spacing for Reference Number column
    },
    StockIn_UOM: {
      //paddingRight: '80px', // Adjust spacing for Stock In column
    },
    StockOut_UOM: {
      //paddingRight: '80px', // Adjust spacing for Stock Out column
    },
    Current_Stock_UOM: {
      //paddingLeft: '80px', // Adjust spacing for Current Stock column
    },
  };

  return (
    <div>
      <Header />
      <Page>
        <Row className="mb-3">
          <Col xs={12} className="mb-1">
            <Title pageTitle="Raw Materials List" />
          </Col>
  
          <Col xs={12}>
            <Card color="primary" outline>
              <CardBody>
                {/* Search Filters Section */}
                <FormGroup row>
                  <Label for="fromDate" sm={2}>From Date</Label>
                  <Col sm={4}>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date) => setFromDate(date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select From Date"
                      className="form-control"
                      isClearable
                    />
                  </Col>
                  
                  <Label for="toDate" sm={2}>To Date</Label>
                  <Col sm={4}>
                    <DatePicker
                      selected={toDate}
                      onChange={(date) => setToDate(date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Select To Date"
                      className="form-control"
                      isClearable
                    />
                  </Col>
                  
                  
                </FormGroup>
  
                <FormGroup row>
                  <Label for="productName" sm={2}>Product Name</Label>
                  <Col sm={4}>
                    <Input
                      type="select"
                      id="productName"
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value === 'all' ? 0 : e.target.value)}  // Handle "All" as 0
                    >
                      <option value="0">All</option>
                      {productNames.map((product) => (
                        <option key={product.raw_material_id} value={product.raw_material_id}>
                          {product.raw_material_name} - {product.raw_material_id}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col sm={4}>
                    <Button color="primary" onClick={getFilteredStatusData}>
                      Filter Status
                    </Button>
                  </Col>
                </FormGroup>
  
                {/* Table */}
                {loading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                  </div>
                ) : tableData.length === 0 ? (
                  <div className="text-center text-danger">No raw material data available.</div>
                ) : (
                  <DataTable
                    customStyles={custTableStyles} // Apply custom styles
                    columns={[
                      { 
                        name: 'Sl No', 
                        selector: (row) => row.sl,
                        width: '80px',
                        wrap: true, // Wrap text for this column
                      },
                      { 
                        name: 'Raw Material Name', 
                        selector: (row) => row.Raw_Material_Name,
                        wrap: true, // Wrap text for this column
                      },
                      { 
                        name: 'Transaction Date', 
                        selector: (row) => row.Transaction_Date,
                        wrap: true, // Wrap text for this column
                      },
                      { 
                        name: 'Reference Number', 
                        selector: (row) => row.reference_number,
                        wrap: true,
                        textAlign: 'right', // Wrap text for this column
                      },
                      { 
                        name: 'Stock In', 
                        selector: (row) => row.StockIn_UOM,
                        wrap: true, // Wrap text for this column
                      },
                      { 
                        name: 'Stock Out', 
                        selector: (row) => row.StockOut_UOM,
                        wrap: true, // Wrap text for this column
                      },
                      { 
                        name: 'Current Stock', 
                        selector: (row) => row.Current_Stock_UOM,
                        wrap: true, // Wrap text for this column
                      },
                    ]}
                    data={tableData}
                    keyField="sl"
                    conditionalRowStyles={columnStyles} 
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Page>
      <Footer />
    </div>
  );
  
};

export default ViewRaw;
